import { Component, Input, OnDestroy } from '@angular/core';
import { combineLatest, Subject, Subscription, takeUntil } from 'rxjs';
import { GasprecioService } from '../../../gasprecio/services/gasprecio.service';
import { SharedService } from '../../../gasprecio/services/shared.service';
import { Router, Event as NavigationEvent, NavigationEnd } from '@angular/router';
import Highcharts from 'highcharts';
import { GroupIds } from 'src/app/modules/gasprecio/enums/groups-ids.enum';
import moment from 'moment';
import type {
  Chart as HighchartsChart,
  ChartCallbackFunction as HighchartsChartCallback
} from 'highcharts';

@Component({
  selector: 'app-chart-sales-distribution',
  templateUrl: './chart-sales-distribution.component.html',
  styleUrls: ['./chart-sales-distribution.component.scss']
})
export class ChartSalesDistributionComponent implements OnDestroy {

  @Input() variable = 'liters';
  @Input() type = 'Autos';
  @Input() station: any = false;

  stationSelected$!: Subscription;
  stationSelected!: string;

  chartOptions: Highcharts.Options;
  private chartRefSales: HighchartsChart | null = null;
  chartCallbackSales: HighchartsChartCallback = (c) => { this.chartRefSales = c; };
  data = [];
  date = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
  event$!: Subscription;
  Highcharts = Highcharts;
  idGroup: GroupIds | string = GroupIds.Pa;
  loading = false;
  private destroy$ = new Subject<void>();
  productType = 'regular';
  reqst = 0;
  rol;
  route;
  stationId: any;
  period;
  zone;
  changeZone = false;

  constructor(
    private gasprecioService: GasprecioService,
    private sharedService: SharedService,
    private router: Router
  ) {
    this.rol = localStorage.getItem('rol');
    this.idGroup = (this.rol === 'Dios') ? localStorage.getItem('group') || GroupIds.Pa : localStorage.getItem('group');
    this.stationId = localStorage.getItem('idStation');
    this.event$ = this.router.events.subscribe((event: NavigationEvent) => {
      if (event instanceof NavigationEnd) {
        this.route = /[^/]*$/.exec(event.url)[0];
      }
    });

    this.stationSelected$ = this.sharedService.sharedStation.subscribe(stationSelected => {
      if (!this.station && stationSelected !== '') {
        this.stationSelected = stationSelected;
      }
    });

    combineLatest([
      this.sharedService.sharedGroup,
      this.sharedService.sharedZoneHeatMap,
      this.sharedService.sharedStationSelected,
      this.sharedService.sharedSelectedMonth,
      this.sharedService.sharedProductTypeTotal,
    ]).pipe(takeUntil(this.destroy$)).subscribe(([idGroup, zone, stationId, period, productType]) => {
      if (idGroup !== this.idGroup) {
        this.idGroup = idGroup;
      }

      if (this.zone !== zone) {
        if (this.zone !== undefined) {
          this.changeZone = true;
        }
        this.zone = zone;
      }

      if (this.stationId !== stationId) {
        this.stationId = stationId;
      }

      if (period !== this.date) {
        this.date = period;
      }

      if (this.productType !== productType) {
        this.productType = productType;
      }

      if (!this.loading) {
        this.getHeatMapDistribution();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stationSelected$.unsubscribe();
  }

  getHeatMapDistribution() {
    this.loading = true;
    this.reqst = 0;
    this.data = [];

    this.stationSelected$ = this.sharedService.sharedStation.subscribe(stationSelected => {
      if (!this.station && stationSelected !== '') {
        this.stationSelected = stationSelected;
      }
    });

    setTimeout(() => {

      const stationCondition = this.station || (this.stationSelected$ && this.stationSelected !== 'zone');
      let url: any;

      if (stationCondition) {
        url = this.gasprecioService.getHeatMapDistributionByStation(this.idGroup, this.variable, this.date, this.stationSelected);
      } else if (this.stationId === '' || !this.zone) {
        url = this.gasprecioService.getHeatMapDistribution(this.idGroup, this.stationId, this.variable, this.date, false);
      } else {
        url = this.gasprecioService.getHeatMapDistribution(this.idGroup, this.zone, this.variable, this.date, true);
      }



      url.subscribe({
        next: (data: any) => {
          const info = data.reverse();
          info.map((x: any) => {
            this.data.push({
              name: `${this.gasprecioService.capitalizeFirstLetter(x['product'] || '')} - ${this.gasprecioService.numberWithCommas((x['value']?.toFixed(0) || '0'))} lts.`,
              y: +(x['pct']?.toFixed(1) || 0),
              color: x['product'] === 'regular' ? '#159546' :
                x['product'] === 'premium' ? '#ea0000' :
                  x['product'] === 'diesel' ? '#1e2228' : '#cccccc'  // Color de respaldo
            });
          });
          this.setData();
        }, complete: () => {
          this.reqst = 1;
        }, error: () => {
          this.loading = false;
          this.reqst = 2;
        }
      });
    }, 3000);
  }

  setData() {
    this.chartOptions = {
      credits: {
        enabled: false
      },
      chart: {
        type: 'pie',
        plotShadow: false
      },
      title: {
        text: `Distribución de Venta`,
        style: {
          fontSize: '0'
        }
      },
      exporting: {
        filename: `Distribución de Venta`,
        sourceWidth: 1200,
        sourceHeight: 800,
      },
      navigation: {
        buttonOptions: {
          align: 'right',
          y: -10
        }
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
      },
      accessibility: {
        point: {
          valueSuffix: '%'
        }
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'top',
        itemDistance: 10,
        y: 1,
        padding: 1,
        itemMarginTop: 1,
        itemMarginBottom: 1,
        itemHoverStyle: {
          color: '#00E07B',
        }
      },
      xAxis: {
        categories: [],
        gridLineWidth: 1,
        gridLineDashStyle: 'LongDash',
        title: {
          text: 'Fecha',
          style: {
            fontSize: '0'
          }
        },
        labels: {
          style: {
            fontSize: '11px',
          },
          y: 14
        }
      },
      yAxis: [
        {
          title: {
            text: 'Productos',
            style: {
              fontSize: '0'
            }
          },
          labels: {
            format: '{value:,.0f} lts',
            style: {
              fontSize: '11px'
            }
          },
          tickAmount: 10,
          gridLineDashStyle: 'LongDash'
        }
      ],
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f} %',
            distance: 10
          },
          showInLegend: true
        }
      },
      series: [{
        type: 'pie',
        name: 'Ventas',
        data: this.data
      }]
    };

    setTimeout(() => {
      this.loading = false;
    }, 100);
  }

  private async chartToPng(chart: HighchartsChart, scale = 2, fallbackOptions?: Highcharts.Options): Promise<string> {
    const w = 800, h = 600;
    const exportOverrides: Highcharts.Options = {
      plotOptions: {
        series: {
          dataLabels: {
            style: { fontSize: '20px', fontWeight: '600' }
          }
        }
      }
    };

    let svg: string;

    try {
      if (!chart || (chart as any).destroyed) throw new Error('dead');
      const opts = Highcharts.merge(
        { exporting: { sourceWidth: w, sourceHeight: h, scale } },
        exportOverrides
      ) as Highcharts.Options;
      svg = chart.getSVG(opts);
    } catch {
      if (!fallbackOptions) throw new Error('Chart no inicializado y sin fallbackOptions');

      const el = document.createElement('div');
      el.style.position = 'absolute';
      el.style.left = '-99999px';
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
      document.body.appendChild(el);

      try {
        const tmpOpts = Highcharts.merge(
          {},
          fallbackOptions,
          exportOverrides,
          { chart: { width: w, height: h, animation: false },
            exporting: { sourceWidth: w, sourceHeight: h, scale },
            credits: { enabled: false } } as Highcharts.Options
        );
        const tmp = Highcharts.chart(el, tmpOpts);
        svg = tmp.getSVG({ exporting: { sourceWidth: w, sourceHeight: h, scale } });
        tmp.destroy();
      } finally {
        el.remove();
      }
    }
    const svgUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = rej; img.src = svgUrl; });
    const canvas = document.createElement('canvas');
    canvas.width = img.width || 1200 * scale;
    canvas.height = img.height || 600 * scale;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  }
  async toDataURLSales(scale = 2) {
    if (!this.chartRefSales) throw new Error('Chart Sales no inicializado');
    return this.chartToPng(this.chartRefSales, scale, this.chartOptions);
  }
  async toDataURLAll(scale = 2) {
    const [Sales] = await Promise.all([
      this.toDataURLSales(scale),
    ]);
    return { Sales };
  }
}
